// equivalencies/formulas.js
// Calculation functions and smart formatting for environmental equivalencies

import {
  BTU_PER_GALLON,
  BOTTLE_BTU,
  CO2_PER_CAN_G,
  CO2_PER_TREE_10Y_MT,
  MILLION,
  GRAMS_PER_METRIC_TON
} from './constants.js';

/**
 * Energy-based conversions (from Million BTU)
 */
export const toGallonsFromMMBtu = (mmbtu) => {
  if (mmbtu == null || typeof mmbtu !== 'number' || isNaN(mmbtu)) return 0;
  return Math.abs(mmbtu) * MILLION / BTU_PER_GALLON;
};

export const toBottlesFromMMBtu = (mmbtu) => {
  if (mmbtu == null || typeof mmbtu !== 'number' || isNaN(mmbtu)) return 0;
  return Math.abs(mmbtu) * MILLION / BOTTLE_BTU;
};

/**
 * GHG-based conversions (from Metric Tons CO2e)
 */
export const toCansFromMTCO2e = (mtco2e) => {
  if (mtco2e == null || typeof mtco2e !== 'number' || isNaN(mtco2e)) return 0;
  return Math.abs(mtco2e) * GRAMS_PER_METRIC_TON / CO2_PER_CAN_G;
};

export const toTreesFromMTCO2e = (mtco2e) => {
  if (mtco2e == null || typeof mtco2e !== 'number' || isNaN(mtco2e)) return 0;
  return Math.abs(mtco2e) / CO2_PER_TREE_10Y_MT;
};

/**
 * Smart number formatting based on magnitude
 * - >=1000: 0 decimal places
 * - 100-999: 1 decimal place
 * - <100: 2 decimal places
 */
export const formatSmart = (number) => {
  if (number == null || typeof number !== 'number' || isNaN(number)) {
    return '0';
  }

  const absNumber = Math.abs(number);
  
  if (absNumber >= 1000) {
    return absNumber.toFixed(0);
  } else if (absNumber >= 100) {
    return absNumber.toFixed(1);
  } else {
    return absNumber.toFixed(2);
  }
};

/**
 * Determine if a value represents an environmental benefit (negative original value)
 */
export const isBenefit = (originalValue) => {
  return originalValue != null && typeof originalValue === 'number' && originalValue < 0;
};

/**
 * Calculate all equivalencies from totals
 */
export const calculateEquivalencies = (totals) => {
  const mmbtu = totals?.mmbtu || 0;
  const mtco2e = totals?.mtco2e || 0;

  return {
    gasoline: {
      value: toGallonsFromMMBtu(mmbtu),
      rawValue: mmbtu,
      isBenefit: isBenefit(mmbtu)
    },
    plasticBottles: {
      value: toBottlesFromMMBtu(mmbtu),
      rawValue: mmbtu,
      isBenefit: isBenefit(mmbtu)
    },
    aluminumCans: {
      value: toCansFromMTCO2e(mtco2e),
      rawValue: mtco2e,
      isBenefit: isBenefit(mtco2e)
    },
    treeSeedlings: {
      value: toTreesFromMTCO2e(mtco2e),
      rawValue: mtco2e,
      isBenefit: isBenefit(mtco2e)
    }
  };
};

/**
 * Generate copy-friendly factors text
 */
export const generateFactorsText = () => {
  return `Environmental Equivalency Conversion Factors:

Energy Conversions:
• Gasoline: ${BTU_PER_GALLON.toLocaleString()} Btu/gallon
• Plastic Bottles: ${BOTTLE_BTU.toLocaleString()} Btu/bottle

GHG Conversions:
• Aluminum Cans: ${CO2_PER_CAN_G} g CO2e avoided per can recycled
• Tree Seedlings: ${CO2_PER_TREE_10Y_MT} metric tons CO2 sequestered per tree over 10 years

Formulas:
• Gallons = |MMBtu| × 1,000,000 ÷ ${BTU_PER_GALLON.toLocaleString()}
• Bottles = |MMBtu| × 1,000,000 ÷ ${BOTTLE_BTU.toLocaleString()}
• Cans = |MTCO2e| × 1,000,000 ÷ ${CO2_PER_CAN_G}
• Trees = |MTCO2e| ÷ ${CO2_PER_TREE_10Y_MT}

Note: All calculations use absolute values to show environmental impact magnitude.`;
};
